
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getAvatarUrl } from '@/utils/storage';

interface FeaturedPractitioner {
  id: number;
  name: string;
  specialization: string;
  avatarUrl: string;
  profileUrl: string;
  rating: number;
  reviewCount: number;
  bio: string;
  services: string[];
}


export const useFeaturedPractitioners = () => {
  const [practitioners, setPractitioners] = useState<FeaturedPractitioner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFeaturedPractitioners = async () => {
      try {
        setLoading(true);

        // 先尝试读取 featured_practitioners（最多3个，按 sort_order）
        console.log('🔍 Fetching featured practitioners...');
        const { data: featured, error: featErr } = await supabase
          .from('featured_practitioners')
          .select('practitioner_id, sort_order')
          .eq('is_active', true)
          .order('sort_order', { ascending: true })
          .limit(3);
        
        console.log('📝 Featured practitioners query result:', { 
          count: featured?.length || 0, 
          featured: featured?.map(f => ({ id: f.practitioner_id, order: f.sort_order })), 
          error: featErr 
        });
        if (featErr) throw featErr;

        let baseData: any[] = [];
        if (featured && featured.length > 0) {
          console.log('📝 Found featured practitioners, fetching details...');
          const ids = featured.map(f => f.practitioner_id);
          console.log('📝 Looking for practitioner IDs:', ids);
          
          // First check if any practitioners exist with these IDs
          const { data: allPractitioners, error: checkErr } = await supabase
            .from('practitioners')
            .select('id, first_name, last_name, profile_status, is_verified, accepting_new_clients')
            .in('id', ids);
          
          console.log('📝 All practitioners check:', { 
            found: allPractitioners?.length || 0,
            practitioners: allPractitioners?.map(p => ({
              id: p.id,
              name: `${p.first_name} ${p.last_name}`,
              status: p.profile_status,
              verified: p.is_verified,
              accepting: p.accepting_new_clients
            }))
          });

          const { data: practitionersData, error: pracErr } = await supabase
            .from('practitioners')
            .select('id, first_name, last_name, professional_title, bio, avatar_url, average_rating, total_reviews, slug, profile_status, is_verified, accepting_new_clients')
            .in('id', ids)
            .eq('profile_status', 'approved')
            .eq('is_verified', true)
            .eq('accepting_new_clients', true);
          
          console.log('📝 Filtered practitioners query result:', { 
            count: practitionersData?.length || 0,
            practitioners: practitionersData?.map(p => ({ 
              id: p.id, 
              name: `${p.first_name} ${p.last_name}`,
              slug: p.slug
            })),
            error: pracErr 
          });
          if (pracErr) throw pracErr;
          
          // Sort by featured order
          baseData = (practitionersData || []).sort((a, b) => {
            const aOrder = featured.find(f => f.practitioner_id === a.id)?.sort_order || 999;
            const bOrder = featured.find(f => f.practitioner_id === b.id)?.sort_order || 999;
            return aOrder - bOrder;
          });
        } 
        
        // If no valid practitioners found from featured list, use fallback
        if (baseData.length === 0) {
          console.log('📝 No valid featured practitioners found, using fallback...');
          // 回退到公共列表
          const { data, error } = await supabase
            .rpc('get_public_practitioners')
            .order('total_reviews', { ascending: false })
            .order('average_rating', { ascending: false })
            .order('first_name', { ascending: true })
            .limit(3);
          
          console.log('📝 Fallback query result:', { 
            count: data?.length || 0,
            practitioners: data?.map(p => ({ 
              id: p.id, 
              name: `${p.first_name} ${p.last_name}`,
              slug: p.slug
            })),
            error 
          });
          if (error) throw error;
          baseData = data || [];
        }

        const ids = (baseData || []).map((p: any) => p.id);
        const { data: services, error: svcErr } = await supabase
          .from('services')
          .select('practitioner_id, service_name, is_active')
          .in('practitioner_id', ids)
          .eq('is_active', true);
        if (svcErr) throw svcErr;

        const servicesByPractitioner = new Map<string, string[]>();
        (services || []).forEach((s: any) => {
          const arr = servicesByPractitioner.get(s.practitioner_id) || [];
          arr.push(s.service_name);
          servicesByPractitioner.set(s.practitioner_id, arr);
        });

        console.log('📝 Final baseData before transformation:', {
          count: baseData?.length || 0,
          practitioners: baseData?.map(p => ({ 
            id: p.id, 
            name: `${p.first_name} ${p.last_name}`,
            hasSlug: !!p.slug
          }))
        });

        const transformedData: FeaturedPractitioner[] = (baseData || []).map((practitioner: any, index: number) => {
          // Resolve avatar URL: if it's a storage path, convert to public URL
          let avatarUrl = practitioner.avatar_url || '';
          if (avatarUrl && !(avatarUrl.startsWith('http') || avatarUrl.startsWith('data:'))) {
            avatarUrl = getAvatarUrl('practitioner.photo', avatarUrl);
          }

          const transformed = {
            id: index + 1,
            name: `${practitioner.first_name || ''} ${practitioner.last_name || ''}`.trim() || 'Professional',
            specialization: practitioner.professional_title || 'Spiritual Practitioner',
            avatarUrl: avatarUrl || '/placeholder.svg',
            profileUrl: `/book-an-appointment/${practitioner.slug || practitioner.id}`,
            rating: Number(practitioner.average_rating) || 0,
            reviewCount: practitioner.total_reviews || 0,
            bio: practitioner.bio || 'Experienced practitioner helping clients achieve balance and wellness.',
            services: servicesByPractitioner.get(practitioner.id) || []
          };
          
          console.log('📝 Transformed practitioner:', {
            original_id: practitioner.id,
            name: transformed.name,
            profileUrl: transformed.profileUrl,
            services: transformed.services
          });
          
          return transformed;
        });

        console.log('📝 Final transformed data:', {
          count: transformedData.length,
          practitioners: transformedData.map(p => ({ id: p.id, name: p.name }))
        });

        setPractitioners(transformedData);
      } catch (err) {
        console.error('Error fetching featured practitioners:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch practitioners');
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedPractitioners();
  }, []);

  return { practitioners, loading, error };
};
